/**
 * Date formatting utilities with consistent cross-environment behavior
 */

import { splitDuration, formatDurationHuman } from "./duration";

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const LONG_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Formats a date string to a short format (e.g., "Jan 1, 2024")
 * Uses deterministic formatting to avoid locale-specific issues in tests/CI
 */
export function formatDateShort(dateString: string): string {
  const date = new Date(dateString);
  return `${SHORT_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

/**
 * Formats a date string to a long format (e.g., "January 1, 2024")
 */
export function formatDateLong(dateString: string): string {
  const date = new Date(dateString);
  return `${LONG_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

/**
 * Formats a date string to include time (e.g., "Jan 1, 2024 at 2:30 PM")
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  const dateStr = formatDateShort(dateString);
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${dateStr} at ${timeStr}`;
}

/**
 * Formats a date for input fields (YYYY-MM-DD)
 */
export function formatDateForInput(dateString: string): string {
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
}

/**
 * Formats a date string with local date and time (e.g. "1/1/2024, 2:30 PM"),
 * returning the caller's fallback for null/empty/unparseable input.
 */
export function formatDateTimeOptional(
  dateString: string | null | undefined,
  fallback: string,
): string {
  if (!dateString) return fallback;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString();
}

/**
 * Formats a date for datetime-local input fields in local time
 * (YYYY-MM-DDTHH:mm)
 */
export function formatDateTimeLocalInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Formats a time duration in milliseconds to human readable format
 * (hours are shown without seconds: "1h 1m")
 */
export function formatDuration(milliseconds: number): string {
  const { hours, minutes } = splitDuration(Math.floor(milliseconds / 1000));
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return formatDurationHuman(Math.floor(milliseconds / 1000));
}

/**
 * Formats a session date string with a locale-aware long date and 24-hour
 * time (e.g., "January 1, 2024 at 10:00")
 */
export function formatSessionDateTime(dateString: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(dateString));
}

/**
 * Formats elapsed time in milliseconds to HH:MM:SS or MM:SS format
 * @param ms - Time in milliseconds
 * @returns Formatted time string (e.g., "05:23" or "01:15:42")
 */
export function formatElapsedTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}
